import type { ReactNode } from 'react';

/**
 * Kleine Symbole für die Werbeseiten (Startseite, Funktionen, Basar-Kalender). Rein dekorativ: immer
 * `aria-hidden`, die Aussage steht im Text daneben. Farbe nur über `currentColor` bzw. Design-Tokens,
 * damit helles und dunkles Design passen (keine festen Farbwerte).
 */
const PATHS = {
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2 20a7 7 0 0 1 14 0M16 4.5a3.5 3.5 0 0 1 0 7M18 13.5a7 7 0 0 1 4 6.5" />
    </>
  ),
  tag: (
    <>
      <path d="M3 12V4a1 1 0 0 1 1-1h8l9 9-9 9z" />
      <circle cx="7.5" cy="7.5" r="1.5" />
    </>
  ),
  register: (
    <>
      <rect x="3" y="10" width="18" height="11" rx="2" />
      <path d="M7 10V4h10v6M7 14h2M11 14h2M15 14h2M7 17.5h10" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12l3 3 5-6" />
    </>
  ),
  cross: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </>
  ),
  offline: (
    <>
      <path d="M5 12.5a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0M2 9a15 15 0 0 1 20 0" />
      <circle cx="12" cy="19.5" r="0.8" />
      <path d="M3 3l18 18" />
    </>
  ),
  browser: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M6.5 6.5h.01M9.5 6.5h.01" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  euro: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M15.5 8.5a4.5 4.5 0 1 0 0 7M7 11h6M7 13.5h6" />
    </>
  ),
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3h-3zM21 14v3M14 21h7M21 19v2" />
    </>
  ),
  scan: (
    <>
      <path d="M3 7V4h3M18 4h3v3M21 17v3h-3M6 20H3v-3" />
      <path d="M7 8v8M10 8v8M13.5 8v8M17 8v8" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
  school: (
    <>
      <path d="M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3.5 2.5 8.5 2.5 12 0v-5" />
    </>
  ),
  blocks: (
    <>
      <rect x="3.5" y="13" width="7.5" height="7.5" rx="1" />
      <rect x="13" y="13" width="7.5" height="7.5" rx="1" />
      <rect x="8.25" y="3.5" width="7.5" height="7.5" rx="1" />
    </>
  ),
  home: (
    <>
      <path d="M4 11l8-7 8 7v9H4z" />
      <path d="M10 20v-5h4v5" />
    </>
  ),
  chart: <path d="M4 20V11M10 20V5M16 20v-7M2.5 20.5h19" />,
  printer: (
    <>
      <path d="M7 8V3h10v5" />
      <rect x="3" y="8" width="18" height="9" rx="2" />
      <rect x="7" y="14" width="10" height="7" rx="1" />
    </>
  ),
  sync: (
    <>
      <path d="M20 11a8 8 0 0 0-14.5-4.5M4 3v4h4" />
      <path d="M4 13a8 8 0 0 0 14.5 4.5M20 21v-4h-4" />
    </>
  ),
  phone: (
    <>
      <rect x="6.5" y="2" width="11" height="20" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s-7-6.5-7-12a7 7 0 0 1 14 0c0 5.5-7 12-7 12z" />
      <circle cx="12" cy="9" r="2.5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  list: <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />,
  share: (
    <>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </>
  ),
  file: (
    <>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6M8 13h8M8 17h5" />
    </>
  ),
  upload: <path d="M12 16V4M7 9l5-5 5 5M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />,
  database: (
    <>
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
    </>
  ),
  sparkle: (
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" />
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  coins: (
    <>
      <ellipse cx="9" cy="7" rx="6" ry="3" />
      <path d="M3 7v4c0 1.700 2.700 3 6 3s6-1.300 6-3V7M15 12.500c3.300 0 6 1.200 6 2.500v3c0 1.700-2.700 3-6 3-1.800 0-3.400-.4-4.500-1" />
    </>
  ),
  receipt: (
    <>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6M9 12h6" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.900 4.900l1.400 1.400M17.700 17.700l1.400 1.400M2 12h2M20 12h2M4.900 19.100l1.400-1.400M17.700 6.300l1.400-1.400" />
    </>
  ),
  moon: <path d="M20 14.500A8 8 0 0 1 9.500 4 8 8 0 1 0 20 14.500z" />,
  monitor: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </>
  ),
  'user-plus': (
    <>
      <circle cx="9" cy="8" r="4" />
      <path d="M2 21a7 7 0 0 1 14 0M19 8v6M16 11h6" />
    </>
  ),
  percent: (
    <>
      <path d="M19 5L5 19" />
      <circle cx="7" cy="7" r="2" />
      <circle cx="17" cy="17" r="2" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6L6 18" />,
  'chevron-left': <path d="M15 5l-7 7 7 7" />,
  'chevron-down': <path d="M6 9l6 6 6-6" />,
  'chevron-up': <path d="M6 15l6-6 6 6" />,
  alert: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16h.01" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  copy: (
    <>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </>
  ),
  trash: <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6" />,
  edit: <path d="M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4" />,
  'chevron-right': <path d="M9 5l7 7-7 7" />,
  // Kasse (U17a): Kopfzeile, Menü und Kamerabild
  park: <path d="M8 21V4h5.500a4.500 4.500 0 0 1 0 9H8" />,
  undo: <path d="M9 14L4 9l5-5M4 9h10.500a5.500 5.500 0 0 1 0 11H11" />,
  more: <path d="M5 12h.01M12 12h.01M19 12h.01" strokeWidth="3.500" />,
  flash: <path d="M13 2L4 14h7l-1 8 9-12h-7z" />,
  'camera-switch': (
    <>
      <path d="M4 7h3l2-3h6l2 3h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z" />
      <path d="M9.500 13a2.700 2.700 0 0 1 4.600-1.600M14.500 13a2.700 2.700 0 0 1-4.600 1.600M14.200 9.800v1.800h-1.800M9.800 16.200v-1.800h1.800" />
    </>
  ),
  volume: <path d="M4 9v6h4l5 4V5L8 9zM16.500 9a4 4 0 0 1 0 6M19 6.500a8 8 0 0 1 0 11" />,
  'volume-off': <path d="M4 9v6h4l5 4V5L8 9zM17 9.500l5 5M22 9.500l-5 5" />,
  fullscreen: <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />,
  download: <path d="M12 4v12M7 11l5 5 5-5M4 20h16" />,
  wallet: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h13v4" />
      <path d="M3 7v11a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1H5a2 2 0 0 1-2-2z" />
      <path d="M16 14.500h.01" />
    </>
  ),
  swap: <path d="M7 4L3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7" />,
  logout: <path d="M9 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h4M16 8l4 4-4 4M20 12H9" />,
  keyboard: (
    <>
      <rect x="2.500" y="6" width="19" height="12" rx="2" />
      <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M7 14h10" />
    </>
  ),
  // Aktionsknöpfe (Knöpfe mit Symbol): Dokumente, Navigation, Hinweise
  pdf: (
    <>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6M8 17v-4h1.500a1.200 1.200 0 0 1 0 2.400H8M13 17v-4M13 13h2.500M13 15h2" />
    </>
  ),
  table: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M3 15h18M9 4v16" />
    </>
  ),
  save: (
    <>
      <path d="M5 3h11l4 4v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
      <path d="M7 3v5h8V3M7 21v-7h10v7" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  'arrow-left': <path d="M20 12H4M10 6l-6 6 6 6" />,
  'arrow-right': <path d="M4 12h16M14 6l6 6-6 6" />,
  external: (
    <path d="M14 4h6v6M20 4l-9 9M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
  ),
  eye: (
    <>
      <path d="M2 12s3.500-7 10-7 10 7 10 7-3.500 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  filter: <path d="M3 5h18l-7 8v6l-4 2v-8z" />,
  archive: (
    <>
      <rect x="3" y="4" width="18" height="5" rx="1" />
      <path d="M5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9M10 13h4" />
    </>
  ),
  bell: <path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 21h4" />,
  flag: <path d="M5 21V4M5 4h12l-2 4 2 4H5" />,
  key: (
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="M11 12l9-9M16 7l3 3M14 9l2 2" />
    </>
  ),
  link: (
    <path d="M10 14a4 4 0 0 0 5.600 0l3-3a4 4 0 0 0-5.600-5.600l-1 1M14 10a4 4 0 0 0-5.600 0l-3 3a4 4 0 0 0 5.600 5.600l1-1" />
  ),
  camera: (
    <>
      <path d="M4 7h3l2-3h6l2 3h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13" r="3.500" />
    </>
  ),
  clipboard: (
    <>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4V3h6v1M9 9h6M9 13h6M9 17h4" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof PATHS;

/** Symbol in Textgröße (Strichstärke 2, Farbe aus `currentColor`). */
export function Icon({ name, size = 24 }: { name: IconName; size?: number }) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}

/** Symbol in einem farbigen Kreis, etwa über Karten oder neben Abschnittsüberschriften. */
export function IconBadge({ name, large = false }: { name: IconName; large?: boolean }) {
  return (
    <span className={large ? 'icon-badge icon-badge--lg' : 'icon-badge'}>
      <Icon name={name} size={large ? 32 : 24} />
    </span>
  );
}

/**
 * Titelgrafik (dekorativ): Kleidungsstück mit Etikett und Smartphone-Kasse mit Häkchen, dazu ein
 * Symbol für „läuft auch offline“. Nur Token-Farben, damit sie in beiden Designs passt.
 */
export function HeroArt() {
  const barcode = [0, 4, 7, 12, 15, 19, 24, 27, 31, 36, 40, 43, 47, 52];
  return (
    <svg
      className="hero-art"
      viewBox="0 0 360 300"
      aria-hidden="true"
      focusable="false"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="180" cy="150" r="132" fill="var(--color-primary-soft)" />
      <circle cx="44" cy="70" r="6" fill="var(--color-primary)" opacity="0.35" />
      <circle cx="326" cy="250" r="9" fill="var(--color-primary)" opacity="0.25" />
      <circle cx="70" cy="252" r="4" fill="var(--color-primary)" opacity="0.45" />

      {/* Kleidungsstück */}
      <path
        d="M40 96l30-22h16a14 14 0 0 0 28 0h16l30 22-14 28-16-8v100H70V116l-16 8z"
        fill="var(--color-surface)"
        stroke="var(--color-primary)"
        strokeWidth="3"
      />
      <path d="M78 150h44M78 168h30" stroke="var(--color-divider)" strokeWidth="4" />

      {/* Smartphone mit Kasse */}
      <rect
        x="226"
        y="58"
        width="104"
        height="186"
        rx="16"
        fill="var(--color-surface)"
        stroke="var(--color-text-muted)"
        strokeWidth="3"
      />
      <rect x="240" y="78" width="76" height="14" rx="5" fill="var(--color-primary-soft)" />
      {[106, 124, 142].map((y) => (
        <g key={y}>
          <rect x="240" y={y} width="44" height="8" rx="4" fill="var(--color-divider)" />
          <rect x="294" y={y} width="22" height="8" rx="4" fill="var(--color-divider)" />
        </g>
      ))}
      <path d="M240 162h76" stroke="var(--color-divider)" strokeWidth="2" />
      <rect x="240" y="174" width="76" height="26" rx="8" fill="var(--color-primary)" />
      <path d="M268 187l6 6 12-12" fill="none" stroke="var(--color-on-primary)" strokeWidth="3" />
      <path d="M266 228h24" stroke="var(--color-divider)" strokeWidth="4" />

      {/* Etikett am Kleidungsstück */}
      <path
        d="M122 118c10 8 20 22 30 36"
        fill="none"
        stroke="var(--color-text-muted)"
        strokeWidth="2"
      />
      <g transform="rotate(-8 186 190)">
        <rect
          x="140"
          y="150"
          width="92"
          height="74"
          rx="10"
          fill="var(--color-surface)"
          stroke="var(--color-primary)"
          strokeWidth="3"
        />
        <circle cx="154" cy="164" r="4" fill="none" stroke="var(--color-primary)" strokeWidth="2" />
        {barcode.map((x, index) => (
          <rect
            key={x}
            x={166 + x}
            y="160"
            width={index % 3 === 0 ? 3 : 2}
            height="32"
            fill="var(--color-text)"
          />
        ))}
        <rect x="152" y="202" width="40" height="8" rx="4" fill="var(--color-divider)" />
        <rect x="200" y="200" width="22" height="12" rx="4" fill="var(--color-primary)" />
      </g>

      {/* Läuft auch offline */}
      <circle cx="322" cy="62" r="24" fill="var(--color-primary)" />
      <g transform="translate(310 50)" fill="none" stroke="var(--color-on-primary)" strokeWidth="2">
        {PATHS.sync}
      </g>
    </svg>
  );
}

/**
 * Kleine Begrüßungsgrafik für die Startseite (dekorativ): Kalenderblatt mit Etikett und Häkchen.
 * Nur Token-Farben, damit sie im hellen und dunklen Design passt.
 */
export function WelcomeArt() {
  return (
    <svg
      className="welcome-art"
      viewBox="0 0 160 120"
      aria-hidden="true"
      focusable="false"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="84" cy="62" r="52" fill="var(--color-primary-soft)" />
      <circle cx="18" cy="26" r="4" fill="var(--color-primary)" opacity="0.35" />
      <circle cx="148" cy="100" r="5" fill="var(--color-primary)" opacity="0.3" />
      <rect
        x="38"
        y="26"
        width="70"
        height="74"
        rx="10"
        fill="var(--color-surface)"
        stroke="var(--color-primary)"
        strokeWidth="3"
      />
      <path d="M38 46h70" stroke="var(--color-primary)" strokeWidth="3" />
      <path d="M58 20v12M88 20v12" stroke="var(--color-primary)" strokeWidth="3" />
      <rect x="50" y="56" width="14" height="10" rx="3" fill="var(--color-divider)" />
      <rect x="70" y="56" width="14" height="10" rx="3" fill="var(--color-divider)" />
      <rect x="90" y="56" width="10" height="10" rx="3" fill="var(--color-primary)" />
      <rect x="50" y="74" width="14" height="10" rx="3" fill="var(--color-divider)" />
      <rect x="70" y="74" width="14" height="10" rx="3" fill="var(--color-divider)" />
      <g transform="rotate(10 120 76)">
        <rect
          x="100"
          y="56"
          width="42"
          height="34"
          rx="6"
          fill="var(--color-surface)"
          stroke="var(--color-text-muted)"
          strokeWidth="2.5"
        />
        <circle cx="108" cy="64" r="2.5" fill="none" stroke="var(--color-text-muted)" />
        {[0, 5, 8, 13, 17, 22, 26].map((x) => (
          <rect key={x} x={108 + x} y="70" width="2" height="12" fill="var(--color-text)" />
        ))}
      </g>
      <circle cx="116" cy="34" r="12" fill="var(--color-primary)" />
      <path d="M110 34l4 4 8-8" fill="none" stroke="var(--color-on-primary)" strokeWidth="3" />
    </svg>
  );
}

/** Leere-Liste-Grafik (dekorativ): offene Kiste, z. B. für „Noch kein Basar“. */
export function EmptyArt() {
  return (
    <svg
      className="empty-art"
      viewBox="0 0 120 90"
      aria-hidden="true"
      focusable="false"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx="60" cy="80" rx="42" ry="6" fill="var(--color-surface-muted)" />
      <path
        d="M24 40l36-14 36 14v32l-36 12-36-12z"
        fill="var(--color-surface)"
        stroke="var(--color-primary)"
        strokeWidth="3"
      />
      <path
        d="M24 40l36 14 36-14M60 54v30"
        fill="none"
        stroke="var(--color-primary)"
        strokeWidth="3"
      />
      <path d="M60 20v-8M44 24l-5-6M76 24l5-6" stroke="var(--color-text-muted)" strokeWidth="3" />
    </svg>
  );
}

/** Ablauf in vier Schritten (dekorativ): Anlegen, Etiketten, Verkauf, Abrechnung, verbunden durch eine Linie. */
export function FlowArt() {
  const steps: IconName[] = ['calendar', 'tag', 'euro', 'file'];
  return (
    <svg
      className="flow-art"
      viewBox="0 0 320 64"
      aria-hidden="true"
      focusable="false"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M32 32h256" stroke="var(--color-divider)" strokeWidth="4" strokeDasharray="2 8" />
      {steps.map((name, index) => (
        <g key={name} transform={`translate(${String(index * 88 + 8)} 8)`}>
          <circle cx="24" cy="24" r="24" fill="var(--color-primary-soft)" />
          <g transform="translate(12 12)" fill="none" stroke="var(--color-primary)" strokeWidth="2">
            {PATHS[name]}
          </g>
        </g>
      ))}
    </svg>
  );
}
